import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import type {MetaType} from '../../../components/Datatable/Datatable'
import {Button} from '../../../components/Button/Button'
import useModals from '../../../hooks/useModals'
import {DELETE, GET, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {ModalDelete} from '../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {HotelExperienceTypeFormatted, getTypeLabel} from './types'

const HotelExperiencesContent: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [experiences, setExperiences] = useState<HotelExperienceTypeFormatted[]>([])
  const [id, setId] = useState<number | null>(null)
  const [, setMeta] = useState<MetaType | undefined>(undefined)

  function showPrice(value: string) {
    if (value && parseFloat(value) > 0) {
      return `R$ ${parseFloat(value).toFixed(2)}`
    }
    return i18n.t('general.paymentFree')
  }

  const getExperiences = async () => {
    try {
      setLoading(true)
      const res = await GET('/hotel-experiences')
      const formattedExperiences = res.data.map((experience: any) => ({
        ...experience,
        typeLabel: getTypeLabel(experience.type),
        priceFormatted: showPrice(experience.price),
        statusLabel: experience.status
          ? i18n.t('hospitality_experiences.status_active')
          : i18n.t('hospitality_experiences.status_inactive'),
        shortDescription:
          experience.description.length > 50
            ? experience.description.substring(0, 50) + '...'
            : experience.description,
        ratingFormatted: experience.rating
          ? `${experience.rating}/5`
          : i18n.t('hospitality_experiences.no_rating'),
        includesFormatted:
          experience.includes.length > 0
            ? experience.includes.slice(0, 2).join(', ') +
              (experience.includes.length > 2 ? '...' : '')
            : i18n.t('hospitality_experiences.no_includes'),
        pluginSpaceName: experience.pluginSpace?.name ?? '',
      }))
      setExperiences(formattedExperiences)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteExperience = async () => {
    try {
      setLoading(true)
      const response = await DELETE(`/hotel-experiences/${id}`)
      if (response.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_experiences.msg_success_delete')),
          text: String(i18n.t('hospitality_experiences.msg_success_delete_text')),
        }).then(async () => await getExperiences())
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('hospitality_experiences.msg_error_delete')),
          text: String(i18n.t('hospitality_experiences.msg_error_delete_text')),
        }).then(async () => await getExperiences())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_experiences.msg_error_delete')),
        text: String(i18n.t('hospitality_experiences.msg_error_delete_text')),
      }).then(async () => await getExperiences())
      console.log(e)
    }
  }

  useEffect(() => {
    getExperiences()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const switchStatus = async (experience: HotelExperienceTypeFormatted, status: boolean) => {
    try {
      const switchExperience = await PUT(`/hotel-experiences/${experience.id}`, {
        status: !status,
      })
      if (switchExperience.success) {
        const newExperiences = experiences.map((exp) => {
          if (exp.id === experience.id) {
            exp.status = !status
            exp.statusLabel = !status
              ? i18n.t('hospitality_experiences.status_active')
              : i18n.t('hospitality_experiences.status_inactive')
          }
          return exp
        })
        setExperiences(newExperiences)
      }
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_experiences.list_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_experiences.list_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t('hospitality_experiences.btn_add')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('criar')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={experiences}
                headers={[
                  {
                    key: i18n.t('hospitality_experiences.field_title'),
                    value: 'title',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_experiences.field_type'),
                    value: 'typeLabel',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_experiences.header_order'),
                    value: 'order',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_experiences.field_duration'),
                    value: 'duration',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_experiences.field_price'),
                    value: 'priceFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_experiences.field_rating'),
                    value: 'ratingFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('hospitality_experiences.field_includes'),
                    value: 'includesFormatted',
                    type: 'default',
                  },
                  {
                    key: i18n.t('general.active'),
                    value: 'status',
                    type: 'check',
                    action: (item) => switchStatus(item, item.status),
                  },
                  {key: i18n.t('hospitality_experiences.field_actions'), value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`editar/${item.id}`)
                    },
                    title: i18n.t('hospitality_experiences.btn_edit'),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(i18n.t('hospitality_experiences.btn_delete'))
                      setId(item.id)
                    },
                    title: i18n.t('hospitality_experiences.btn_delete'),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteExperience()} />
        </div>
      )}
    </>
  )
}

const HotelExperiences: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true},
          {
            title: i18n.t('hospitality_experiences.list_title'),
            path: 'hospitality/hotel-experiences',
            isActive: true,
          },
        ]}
      >
        {i18n.t('hospitality_experiences.list_title')}
      </PageTitle>
      <HotelExperiencesContent />
    </>
  )
}

export {HotelExperiences}
